import React, { useCallback, useId } from 'react';
import { connect } from 'react-redux';
import { useAppContext } from 'src/components/app-context';
import Changelog from '../../../../../changelog/desktop-wrapper';
import {
  openLists as openListsAction,
  closeLists as closeListsAction,
} from '../../../../actions/lists';
import {
  openAlerts as openAlertsAction,
  closeAlerts as closeAlertsAction,
} from '../../../../actions/alert';
import { toggleFilter } from '../../../../actions/filter';
import { trackOpeningSidebar, trackClosingSidebar } from '../../../../tracking';
import { SIDEBAR_STATE, DEFAULT_SIDEBAR_STATE } from '../../../../constants';
import type { AppState } from '../../../../../../reducers';
import * as S from './styles';
import { IconFilter } from '@prisjakt/ui/icon/filter';
import { IconHeartFilled } from '@prisjakt/ui/icon/heart-filled';
import { IconHeartOutlined } from '@prisjakt/ui/icon/heart-outlined';
import { IconNotificationFilled } from '@prisjakt/ui/icon/notification-filled';
import { IconNotificationOutlined } from '@prisjakt/ui/icon/notification-outlined';

import { DesktopOnly } from 'src/ui/block';

type StateProps = {
  tab:
    | SIDEBAR_STATE.NONE
    | SIDEBAR_STATE.LISTS
    | SIDEBAR_STATE.ALERTS
    | SIDEBAR_STATE.FILTER;
  previousTab:
    | SIDEBAR_STATE.LISTS
    | SIDEBAR_STATE.ALERTS
    | SIDEBAR_STATE.FILTER;
  showFilter: boolean;
  showHorizontalFilter: boolean;
};

type DispatchProps = {
  closeLists: () => void;
  openLists: () => void;
  openAlerts: () => void;
  toggleFilter: () => void;
};

type Props = StateProps & DispatchProps;

const SidebarMenuBar = ({
  tab = SIDEBAR_STATE.NONE,
  previousTab = SIDEBAR_STATE.LISTS,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  closeLists: dispatchCloseLists = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  openLists: dispatchOpenLists = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  openAlerts: dispatchOpenAlerts = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  toggleFilter: dispatchToggleFilter = () => {},
  showFilter,
  showHorizontalFilter,
}: Props) => {
  const { t } = useAppContext();

  // Generate unique UIDs to use as HTML ids
  const getId = useId();
  const listsTabId = `${getId}-sidebar-desktop-lists`;
  const alertsTabId = `${getId}-sidebar-desktop-alerts`;
  const filterTabId = `${getId}-sidebar-desktop-filter`;

  const isSidebarOpen = tab !== SIDEBAR_STATE.NONE;
  const isListTabActive = tab === SIDEBAR_STATE.LISTS;
  const isAlertsTabActive = tab === SIDEBAR_STATE.ALERTS;
  const isFilterTabActive = tab === SIDEBAR_STATE.FILTER;

  const toggleView = useCallback(
    (state: SIDEBAR_STATE) => {
      switch (state) {
        case tab:
          dispatchCloseLists();
          break;
        case SIDEBAR_STATE.LISTS:
          dispatchOpenLists();
          break;
        case SIDEBAR_STATE.ALERTS:
          dispatchOpenAlerts();
          break;
        case SIDEBAR_STATE.FILTER:
          dispatchToggleFilter();
          break;
        default:
          dispatchCloseLists();
      }
    },
    [
      dispatchCloseLists,
      dispatchOpenAlerts,
      dispatchOpenLists,
      tab,
      dispatchToggleFilter,
    ]
  );

  const handleListsMenuButton = useCallback(() => {
    if (tab === SIDEBAR_STATE.LISTS) {
      trackClosingSidebar(SIDEBAR_STATE.LISTS, 'sidebarListsButton');
    } else {
      trackOpeningSidebar(SIDEBAR_STATE.LISTS, 'sidebarListsButton');
    }

    toggleView(SIDEBAR_STATE.LISTS);
  }, [toggleView, tab]);

  const handleAlertsMenuButton = useCallback(() => {
    if (tab === SIDEBAR_STATE.ALERTS) {
      trackClosingSidebar(SIDEBAR_STATE.ALERTS, 'sidebarAlertsButton');
    } else {
      trackOpeningSidebar(SIDEBAR_STATE.ALERTS, 'sidebarAlertsButton');
    }

    toggleView(SIDEBAR_STATE.ALERTS);
  }, [toggleView, tab]);

  const handleToggleButton = useCallback(() => {
    if (isSidebarOpen) {
      trackClosingSidebar(tab, 'sidebarToggleButton');
      toggleView(SIDEBAR_STATE.NONE);
      return;
    }

    // Open default view if previous view was filter but no longer
    // on a leaf category page, or if filter is set to HorizontalFilter,
    // or `previousTab` is empty.
    if (
      !previousTab ||
      (previousTab === SIDEBAR_STATE.FILTER && !showFilter) ||
      (previousTab === SIDEBAR_STATE.FILTER && showHorizontalFilter)
    ) {
      trackOpeningSidebar(DEFAULT_SIDEBAR_STATE, 'sidebarToggleButton');
      toggleView(DEFAULT_SIDEBAR_STATE);
      return;
    }

    trackOpeningSidebar(previousTab, 'sidebarToggleButton');
    toggleView(previousTab);
  }, [
    isSidebarOpen,
    previousTab,
    showFilter,
    showHorizontalFilter,
    toggleView,
    tab,
  ]);

  return (
    <S.SidebarMenu>
      <S.TopBar>
        <S.MenuButton
          active={false}
          hoverEffect="background"
          onClick={handleToggleButton}
          aria-label={
            isSidebarOpen
              ? t('symbols', 'symbol.collapse-sidebar')
              : t('symbols', 'symbol.expand-sidebar')
          }
          aria-expanded={isSidebarOpen}
          aria-haspopup="true"
        >
          <S.ToggleSidebarIcon size={24} open={isSidebarOpen} />
        </S.MenuButton>

        <S.MenuButton
          active={isListTabActive}
          onClick={handleListsMenuButton}
          data-test="MenuButtonLists"
          aria-label={t('lists', 'lists.title')}
          aria-expanded={isListTabActive}
          aria-haspopup="true"
          aria-controls={listsTabId}
        >
          {isListTabActive ? <IconHeartFilled /> : <IconHeartOutlined />}
        </S.MenuButton>
        <S.MenuButton
          active={isAlertsTabActive}
          onClick={handleAlertsMenuButton}
          data-test="MenuButtonAlerts"
          aria-label={t('alerts', 'alerts.title')}
          aria-expanded={isAlertsTabActive}
          aria-haspopup="true"
          aria-controls={alertsTabId}
        >
          {isAlertsTabActive ? (
            <IconNotificationFilled />
          ) : (
            <IconNotificationOutlined />
          )}
        </S.MenuButton>

        <S.MenuButton
          active={isFilterTabActive}
          onClick={() => toggleView(SIDEBAR_STATE.FILTER)}
          hidden={!showFilter || showHorizontalFilter}
          data-test="MenuButtonFilter"
          aria-label={t('filter', 'filter.title')}
          aria-expanded={isFilterTabActive}
          aria-haspopup="true"
          aria-controls={filterTabId}
        >
          <IconFilter size={21} />
        </S.MenuButton>
      </S.TopBar>
      <DesktopOnly>
        <S.BottomBar>
          <Changelog />
        </S.BottomBar>
      </DesktopOnly>
    </S.SidebarMenu>
  );
};

const mapStateToProps = (state: AppState) => ({
  tab: state.areas.sidebar.tab,
  previousTab: state.areas.sidebar.previousTab,
  showFilter:
    state.areas.category.filter.showFilter || state.areas.sidebar.hasFilter,
  showHorizontalFilter: state.areas.category.filter.showHorizontalFilter,
});

const dispatchProps = {
  openLists: openListsAction,
  closeLists: closeListsAction,
  openAlerts: openAlertsAction,
  closeAlerts: closeAlertsAction,
  toggleFilter,
};

export default connect(mapStateToProps, dispatchProps)(SidebarMenuBar);
